import React, { useEffect, useState } from 'react';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, Save, X, BookOpen, Image as ImageIcon, Calendar,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import api from '../lib/api';

const CATEGORIES = [
  { id: 'vitres', label: 'Vitres' },
  { id: 'terrasse', label: 'Terrasse' },
  { id: 'menage', label: 'Ménage' },
  { id: 'moquette', label: 'Moquette' },
  { id: 'veranda', label: 'Véranda' },
  { id: 'local', label: 'SEO local' },
  { id: 'general', label: 'Général' },
];

const emptyPost = {
  title: '', excerpt: '', content: '', category: 'general',
  cover_image: '', meta_description: '', tags: [], published: true, slug: '',
};

const BlogAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | post
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [tagInput, setTagInput] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/admin/blog')
      .then((res) => setPosts(res.data))
      .catch(() => toast.error('Erreur de chargement des articles'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(emptyPost);
    setEditing('new');
  };

  const openEdit = (post) => {
    setForm({
      title: post.title, excerpt: post.excerpt, content: post.content,
      category: post.category, cover_image: post.cover_image,
      meta_description: post.meta_description, tags: post.tags || [],
      published: post.published, slug: post.slug,
    });
    setEditing(post);
  };

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) handleChange('tags', [...form.tags, t]);
    setTagInput('');
  };

  const removeTag = (t) => handleChange('tags', form.tags.filter((x) => x !== t));

  const handleSave = async () => {
    if (!form.title || !form.excerpt || !form.content || !form.cover_image || !form.meta_description) {
      toast.error('Tous les champs (sauf slug et tags) sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || undefined };
      if (editing === 'new') {
        await api.post('/admin/blog', payload);
        toast.success('Article publié');
      } else {
        await api.patch(`/admin/blog/${editing.id}`, payload);
        toast.success('Article mis à jour');
      }
      setEditing(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur de sauvegarde';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await api.patch(`/admin/blog/${post.id}`, { published: !post.published });
      toast.success(post.published ? 'Article dépublié' : 'Article publié');
      load();
    } catch { toast.error('Échec de la mise à jour'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success('Article supprimé');
      setConfirmDelete(null);
      load();
    } catch { toast.error('Échec de la suppression'); }
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-bold text-slate-900 inline-flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[hsl(207,90%,54%)]" /> Articles du blog
            </h2>
            <p className="text-xs text-slate-500">Créez, modifiez ou dépubliez vos articles</p>
          </div>
          <Button onClick={openNew} data-testid="new-post-button" className="bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,44%)] text-white gap-1.5">
            <Plus className="w-4 h-4" /> Nouvel article
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">Chargement…</div>
        ) : posts.length === 0 ? (
          <div className="py-16 text-center text-slate-400">Aucun article pour le moment</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {posts.map((p) => (
              <li key={p.id} className="p-4 flex items-start gap-4 hover:bg-slate-50/50 transition-colors" data-testid={`post-row-${p.slug}`}>
                <img src={p.cover_image} alt="" className="w-20 h-20 object-cover rounded-lg bg-slate-100 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900 line-clamp-1">{p.title}</h3>
                    {p.published ? (
                      <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">PUBLIÉ</span>
                    ) : (
                      <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">BROUILLON</span>
                    )}
                    <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full uppercase">
                      {CATEGORIES.find(c => c.id === p.category)?.label || p.category}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2">{p.excerpt}</p>
                  <p className="text-xs text-slate-400 mt-1">/blog/{p.slug}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => handleTogglePublish(p)} title={p.published ? 'Dépublier' : 'Publier'}
                          className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid={`toggle-${p.slug}`}>
                    {p.published ? <Eye className="w-4 h-4 text-slate-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                  </button>
                  <button onClick={() => openEdit(p)} title="Modifier"
                          className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-blue-100 hover:text-blue-600 flex items-center justify-center" data-testid={`edit-${p.slug}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setConfirmDelete(p)} title="Supprimer"
                          className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-rose-100 hover:text-rose-600 flex items-center justify-center" data-testid={`delete-${p.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nouvel article' : "Modifier l'article"}</DialogTitle>
            <DialogDescription>
              Le slug (URL) est généré automatiquement à partir du titre si vous le laissez vide.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => handleChange('title', e.target.value)} placeholder="Ex : Comment nettoyer ses vitres sans traces" data-testid="input-title" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Catégorie *</Label>
                <select value={form.category} onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm" data-testid="input-category">
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <Input value={form.slug} onChange={(e) => handleChange('slug', e.target.value)} placeholder="Auto si vide" data-testid="input-slug" />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> URL de l'image de couverture *
              </Label>
              <Input value={form.cover_image} onChange={(e) => handleChange('cover_image', e.target.value)} placeholder="https://…" data-testid="input-cover" />
              {form.cover_image && (
                <img src={form.cover_image} alt="" className="mt-2 h-32 rounded-lg border border-slate-200" onError={(e) => e.target.style.display = 'none'} />
              )}
            </div>

            <div>
              <Label>Résumé (extrait) *</Label>
              <Textarea value={form.excerpt} onChange={(e) => handleChange('excerpt', e.target.value)}
                rows={3} maxLength={500} placeholder="Court résumé (1-2 phrases) affiché dans la liste" data-testid="input-excerpt" />
              <p className="text-xs text-slate-400 mt-1">{form.excerpt.length} / 500</p>
            </div>

            <div>
              <Label>Meta description SEO *</Label>
              <Textarea value={form.meta_description} onChange={(e) => handleChange('meta_description', e.target.value)}
                rows={2} maxLength={200} placeholder="Texte qui apparaîtra dans les résultats Google (150-160 caractères idéal)" data-testid="input-meta" />
              <p className="text-xs text-slate-400 mt-1">{form.meta_description.length} / 200</p>
            </div>

            <div>
              <Label>Contenu (Markdown) *</Label>
              <Textarea value={form.content} onChange={(e) => handleChange('content', e.target.value)}
                rows={15} className="font-mono text-sm" placeholder="## Titre&#10;&#10;Contenu de l'article en **Markdown**..." data-testid="input-content" />
              <p className="text-xs text-slate-400 mt-1">
                Syntaxe Markdown : <code># Titre</code>, <code>**gras**</code>, <code>- liste</code>, <code>[texte](url)</code>
              </p>
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="Appuyez sur Entrée pour ajouter" />
                <Button type="button" variant="outline" onClick={addTag}>Ajouter</Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-medium px-2 py-1 rounded-full">
                      {t} <button onClick={() => removeTag(t)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="published" checked={form.published}
                onChange={(e) => handleChange('published', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300" data-testid="input-published" />
              <Label htmlFor="published" className="cursor-pointer">Publier immédiatement</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-post-button"
              className="bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,44%)] text-white gap-1.5">
              <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet article ?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.title}" sera définitivement supprimé. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(confirmDelete.id)} className="bg-rose-600 hover:bg-rose-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BlogAdmin;
